import "@dxos/node-std/globals"

// packages/core/halo/keyring/src/keyring.ts
import assert from "@dxos/node-std/assert";
import { Event, synchronized } from "@dxos/async";
import { subtleCrypto } from "@dxos/crypto";
import { todo } from "@dxos/debug";
import { PublicKey } from "@dxos/keys";
import { schema } from "@dxos/protocols";
import { createStorage, StorageType } from "@dxos/random-access-storage";
import { ComplexMap } from "@dxos/util";
var __decorate = function(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function")
    r = Reflect.decorate(decorators, target, key, desc);
  else
    for (var i = decorators.length - 1; i >= 0; i--)
      if (d = decorators[i])
        r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Keyring = class {
  constructor(_storage = createStorage({
    type: StorageType.RAM
  }).createDirectory("keyring")) {
    this._storage = _storage;
    this._keyCache = new ComplexMap(PublicKey.hash);
    this.keysUpdate = new Event();
  }
  async sign(key, message) {
    const keyPair = await this._getKey(key);
    return new Uint8Array(await subtleCrypto.sign({
      name: "ECDSA",
      hash: "SHA-256"
    }, keyPair.privateKey, message));
  }
  async createKey() {
    const keyPair = await subtleCrypto.generateKey({
      name: "ECDSA",
      namedCurve: "P-256"
    }, true, [
      "sign",
      "verify"
    ]);
    await this._setKey(keyPair);
    return keyPairToPublicKey(keyPair);
  }
  async _getKey(key) {
    if (!this._keyCache.has(key)) {
      const file = this._storage.getOrCreateFile(key.toHex());
      const { size } = await file.stat();
      if (size === 0) {
        throw new Error(`Key not found: ${key.toHex()}`);
      }
      const recordBytes = await file.read(0, size);
      await file.close();
      const record = schema.getCodecForType("dxos.halo.keyring.KeyRecord").decode(recordBytes);
      const publicKey = PublicKey.from(record.publicKey);
      assert(key.equals(publicKey), "Corrupted keyring: Key mismatch");
      assert(record.privateKey, "Corrupted keyring: Missing private key");
      const keyPair = {
        publicKey: await subtleCrypto.importKey("raw", record.publicKey, {
          name: "ECDSA",
          namedCurve: "P-256"
        }, true, [
          "verify"
        ]),
        privateKey: await subtleCrypto.importKey("pkcs8", record.privateKey, {
          name: "ECDSA",
          namedCurve: "P-256"
        }, true, [
          "sign"
        ])
      };
      this._keyCache.set(publicKey, keyPair);
    }
    return this._keyCache.get(key);
  }
  async _setKey(keyPair) {
    const publicKey = await keyPairToPublicKey(keyPair);
    this._keyCache.set(publicKey, keyPair);
    const record = {
      publicKey: publicKey.asUint8Array(),
      privateKey: new Uint8Array(await subtleCrypto.exportKey("pkcs8", keyPair.privateKey))
    };
    const file = this._storage.getOrCreateFile(publicKey.toHex());
    await file.write(0, Buffer.from(schema.getCodecForType("dxos.halo.keyring.KeyRecord").encode(record)));
    await file.close();
    this.keysUpdate.emit();
  }
  // TODO(burdon): ???
  deleteKey(key) {
    return todo("We need a method to delete a file.");
  }
  list() {
    const keys = [];
    for (const path of this._storage.getFiles().keys()) {
      const fileName = path.split("/").pop();
      assert(fileName, "Invalid file name");
      keys.push({
        publicKey: PublicKey.fromHex(fileName).asUint8Array()
      });
    }
    return keys;
  }
};
__decorate([
  synchronized
], Keyring.prototype, "_getKey", null);
__decorate([
  synchronized
], Keyring.prototype, "_setKey", null);
var keyPairToPublicKey = async (keyPair) => {
  return PublicKey.from(new Uint8Array(await subtleCrypto.exportKey("raw", keyPair.publicKey)));
};
export {
  Keyring
};
//# sourceMappingURL=index.mjs.map
